import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useEffect, useRef } from 'react';

// Default marker icons in Leaflet rely on relative URLs that don't survive bundling. Point them
// at unpkg so the marker actually renders — works in dev (vite) and prod (nginx) without extra
// asset copy steps.
const ICON = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface Props {
  lat: number | null;
  lng: number | null;
  /** Called when the admin clicks the map or drags the marker. */
  onPick: (lat: number, lng: number) => void;
  /** Optional initial centre for new locations (defaults to Mindelo, Cabo Verde). */
  initialCentre?: { lat: number; lng: number };
}

const SANTO_ANTAO: [number, number] = [17.0726, -25.1635]; // Porto Novo
const DEFAULT_ZOOM = 12;

/**
 * Vanilla Leaflet map embedded in the LocationForm. We don't use react-leaflet's component API
 * because it adds a fairly thick re-render layer for a simple pick-a-point UX — the map only
 * needs to react to one prop (the marker position) and emit one event (onPick). Lighter footprint
 * means the modal opens snappier on slow connections at the airport counter.
 */
export function MapPicker({ lat, lng, onPick, initialCentre }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  // Mount once. `onPick` and the seeds (lat/lng/initialCentre) are read via refs to avoid
  // re-creating the map on every change — re-mounting would lose pan/zoom state.
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const seedRef = useRef({ lat, lng, initialCentre });

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once by design
  useEffect(() => {
    if (!containerRef.current) return;
    const seed = seedRef.current;
    const centre: [number, number] =
      seed.lat != null && seed.lng != null
        ? [seed.lat, seed.lng]
        : seed.initialCentre
          ? [seed.initialCentre.lat, seed.initialCentre.lng]
          : SANTO_ANTAO;
    const map = L.map(containerRef.current).setView(centre, DEFAULT_ZOOM);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);

    map.on('click', (e: L.LeafletMouseEvent) => {
      onPickRef.current(e.latlng.lat, e.latlng.lng);
    });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  // Reflect external lat/lng changes into the marker.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (lat == null || lng == null) {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      return;
    }

    const pos: [number, number] = [lat, lng];
    if (!markerRef.current) {
      const marker = L.marker(pos, { icon: ICON, draggable: true }).addTo(map);
      marker.on('dragend', () => {
        const { lat: nLat, lng: nLng } = marker.getLatLng();
        onPick(nLat, nLng);
      });
      markerRef.current = marker;
    } else {
      markerRef.current.setLatLng(pos);
    }
    // Soft-recentre if marker leaves the visible bounds — keeps the user oriented when typing
    // coordinates manually in the address-search field (future).
    if (!map.getBounds().pad(-0.2).contains(L.latLng(lat, lng))) {
      map.panTo(pos, { animate: true });
    }
  }, [lat, lng, onPick]);

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={containerRef}
        style={{ width: '100%', height: 280, border: '1px solid var(--lv-line)' }}
        // biome-ignore lint/a11y/useSemanticElements: leaflet needs a div container
        role="application"
        aria-label="Mapa para escolher localização"
      />
      <p style={{ marginTop: 6, fontSize: 11, color: 'var(--lv-gray-500)' }}>
        Clica no mapa para marcar o ponto. Arrasta o marcador para ajustar.
        {lat != null && lng != null && (
          <span style={{ marginLeft: 6, fontFamily: 'var(--lv-font-mono)' }}>
            {lat.toFixed(6)}, {lng.toFixed(6)}
          </span>
        )}
      </p>
    </div>
  );
}
